/**
 * Campaign delivery aggregation for the Leader "Campaigns" tab.
 *
 * The Leader dashboard's other domains (Salesforce, POV, Workshops) each aggregate a per-person metric
 * keyed by lowercased `OrgChartRow.user_email`; this module produces the equivalent metric for the
 * Campaigns domain. A `CampaignDelivery.owner` is the SE email that owns the delivery, so we bucket
 * every delivery by owner and then, for a manager, roll the whole org subtree up into one figure
 * (`computeSubtreeScope` gives the manager + every descendant). An individual contributor's figure is
 * just their own bucket.
 *
 * Pure module — no React, no fetching. Callers pass the already-fetched deliveries and org rows.
 */
import { computeSubtreeScope, type OrgChartRow } from './orgScope';
import {
  DELIVERY_STATUSES,
  type CampaignDelivery,
  type CampaignDeliveryTarget,
  type CampaignWorkshopMeeting,
  type DeliveryStatus,
} from './campaigns';
import { workshopScheduleStatus } from './campaignWorkshopStatus';

// ---------------------------------------------------------------------------
// Time period (Cisco fiscal calendar window)
// ---------------------------------------------------------------------------

/** An inclusive `YYYY-MM-DD` window (a fiscal month / quarter / year). */
export interface DateWindow {
  start: string;
  end: string;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD` for an ISO timestamp in the viewer's local time (date-only values pass through untouched). */
function localDay(iso: string): string | null {
  if (DATE_ONLY.test(iso)) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The calendar day a delivery is placed on for period filtering: when it is scheduled, else when it was
 * completed. A delivery with neither (an unscheduled draft) has no day, so it only appears under "All".
 */
export function deliveryDay(delivery: Pick<CampaignDelivery, 'scheduled_for' | 'completed_at'>): string | null {
  const iso = delivery.scheduled_for || delivery.completed_at;
  return iso ? localDay(iso) : null;
}

/** The deliveries placed inside `window` (both ends inclusive); `null` = all time, nothing filtered. */
export function filterDeliveriesByWindow(
  deliveries: CampaignDelivery[],
  window: DateWindow | null | undefined,
): CampaignDelivery[] {
  if (!window) return deliveries;
  return deliveries.filter((d) => {
    const day = deliveryDay(d);
    return day != null && day >= window.start && day <= window.end;
  });
}

const SCHEDULABLE: ReadonlySet<DeliveryStatus> = new Set<DeliveryStatus>(['draft', 'in_progress', 'scheduled']);

/**
 * Relabel as `scheduled` every open delivery (draft / in progress) that has a live workshop meeting which
 * has not started yet. Nothing writes `delivery.status = 'scheduled'` (deliveries are created in progress or
 * draft, then completed or cancelled), so counting the stored status alone always reads zero. The rule for a
 * "live, upcoming" meeting is `workshopScheduleStatus` (the analytics pages' definition), and the server
 * rollup applies the same one, so both paths agree. Completed and cancelled deliveries are never overridden.
 * Returns the input untouched when no delivery changes.
 */
export function withScheduledStatus<T extends Pick<CampaignDelivery, 'id' | 'status'>>(
  deliveries: T[],
  meetings: CampaignWorkshopMeeting[] | null | undefined,
  now: number = Date.now(),
): T[] {
  const booked = new Set<number>();
  for (const m of meetings ?? []) {
    if (workshopScheduleStatus(m, now) === 'scheduled') booked.add(m.delivery);
  }
  const relabel = (d: T) => booked.has(d.id) && SCHEDULABLE.has(d.status) && d.status !== 'scheduled';
  if (!deliveries.some(relabel)) return deliveries;
  return deliveries.map((d) => (relabel(d) ? { ...d, status: 'scheduled' } : d));
}

/** Per-owner delivery figure: how many deliveries, split by status. */
export interface DeliveryMetrics {
  total: number;
  byStatus: Record<DeliveryStatus, number>;
  /** How many are open (not completed or cancelled) — the "active" count leaders care about. */
  active: number;
}

const STATUSES: DeliveryStatus[] = DELIVERY_STATUSES.map((s) => s.value);

export function emptyDeliveryMetrics(): DeliveryMetrics {
  const byStatus = {} as Record<DeliveryStatus, number>;
  for (const s of STATUSES) byStatus[s] = 0;
  return { total: 0, byStatus, active: 0 };
}

function add(into: DeliveryMetrics, delivery: CampaignDelivery): void {
  into.total += 1;
  const status = delivery.status;
  if (status in into.byStatus) into.byStatus[status] += 1;
  if (status !== 'completed' && status !== 'cancelled') into.active += 1;
}

/** Sum a set of deliveries into one figure. */
export function sumDeliveries(deliveries: CampaignDelivery[]): DeliveryMetrics {
  const metrics = emptyDeliveryMetrics();
  for (const delivery of deliveries) add(metrics, delivery);
  return metrics;
}

/**
 * Bucket every delivery by its owner (lowercased email). Deliveries with no owner are dropped — they
 * cannot be attributed to a person in the org tree. The returned map is keyed the same way the org
 * rows are matched everywhere else in the Leader pages (`user_email.toLowerCase()`).
 */
export function deliveriesByOwner(deliveries: CampaignDelivery[]): Map<string, CampaignDelivery[]> {
  const byOwner = new Map<string, CampaignDelivery[]>();
  for (const delivery of deliveries) {
    const owner = (delivery.owner ?? '').trim().toLowerCase();
    if (!owner) continue;
    const list = byOwner.get(owner) ?? [];
    list.push(delivery);
    byOwner.set(owner, list);
  }
  return byOwner;
}

/**
 * The deliveries attributable to `email` rolled up over the org subtree it roots — the person's own
 * plus every descendant's. For an individual contributor (a leaf), the subtree is just themselves, so
 * this returns their own deliveries. Used for the manager rollup figure on the team table.
 */
export function subtreeDeliveries(
  email: string,
  orgRows: OrgChartRow[],
  byOwner: Map<string, CampaignDelivery[]>,
): CampaignDelivery[] {
  const scope = computeSubtreeScope(orgRows, email);
  const seen = new Set<number>();
  const out: CampaignDelivery[] = [];
  for (const scopedEmail of scope.emails) {
    const list = byOwner.get(scopedEmail.toLowerCase());
    if (!list) continue;
    for (const delivery of list) {
      // A subtree can list an email once, but guard against duplicate ids defensively.
      if (seen.has(delivery.id)) continue;
      seen.add(delivery.id);
      out.push(delivery);
    }
  }
  return out;
}

/** The subtree rollup figure for `email` (own + all reports). */
export function subtreeMetrics(
  email: string,
  orgRows: OrgChartRow[],
  byOwner: Map<string, CampaignDelivery[]>,
): DeliveryMetrics {
  return sumDeliveries(subtreeDeliveries(email, orgRows, byOwner));
}

// ---------------------------------------------------------------------------
// Pipeline $ (opportunity Amount) by delivery status
// ---------------------------------------------------------------------------

/** Pipeline dollars for a set of deliveries, split by delivery status. */
export interface PipelineMetrics {
  /** Total pipeline $ across every status. */
  total: number;
  byStatus: Record<DeliveryStatus, number>;
  /** How many deliveries are in each status — including those with no opportunity attached. */
  countByStatus: Record<DeliveryStatus, number>;
  /** Of `countByStatus`, how many have no linked opportunity (i.e. contribute $0 so far). */
  noOppByStatus: Record<DeliveryStatus, number>;
  /** Total delivery count across every status. */
  totalCount: number;
}

export function emptyPipelineMetrics(): PipelineMetrics {
  const byStatus = {} as Record<DeliveryStatus, number>;
  const countByStatus = {} as Record<DeliveryStatus, number>;
  const noOppByStatus = {} as Record<DeliveryStatus, number>;
  for (const s of STATUSES) {
    byStatus[s] = 0;
    countByStatus[s] = 0;
    noOppByStatus[s] = 0;
  }
  return { total: 0, byStatus, countByStatus, noOppByStatus, totalCount: 0 };
}

/** The summed opportunity Amount linked to ONE delivery (over all its targets). */
export function deliveryPipeline(
  delivery: CampaignDelivery,
  targetsByDelivery: Map<number, CampaignDeliveryTarget[]>,
  amountByOpp: Map<string, number>,
): number {
  let sum = 0;
  for (const target of targetsByDelivery.get(delivery.id) ?? []) {
    if (target.opportunity) sum += amountByOpp.get(target.opportunity) ?? 0;
  }
  return sum;
}

/**
 * Pipeline $ bucketed by delivery status. For each delivery we sum the Amount of every linked
 * Salesforce opportunity (delivery → targets → `target.opportunity` → `amountByOpp`) and add it to the
 * delivery's status bucket. Deliveries with no linked opportunity contribute 0 to `$` but are still
 * counted (`countByStatus`/`noOppByStatus`) so the leader sees session volume relative to status, not
 * just dollars (Req 5).
 */
export function pipelineByStatus(
  deliveries: CampaignDelivery[],
  targetsByDelivery: Map<number, CampaignDeliveryTarget[]>,
  amountByOpp: Map<string, number>,
): PipelineMetrics {
  const metrics = emptyPipelineMetrics();
  for (const delivery of deliveries) {
    const amount = deliveryPipeline(delivery, targetsByDelivery, amountByOpp);
    if (delivery.status in metrics.byStatus) {
      metrics.byStatus[delivery.status] += amount;
      metrics.countByStatus[delivery.status] += 1;
      const targets = targetsByDelivery.get(delivery.id) ?? [];
      if (!targets.some((t) => !!t.opportunity)) metrics.noOppByStatus[delivery.status] += 1;
    }
    metrics.total += amount;
    metrics.totalCount += 1;
  }
  return metrics;
}

/** Pipeline $ rolled up over the subtree `email` roots (own + all reports), split by status. */
export function subtreePipeline(
  email: string,
  orgRows: OrgChartRow[],
  byOwner: Map<string, CampaignDelivery[]>,
  targetsByDelivery: Map<number, CampaignDeliveryTarget[]>,
  amountByOpp: Map<string, number>,
): PipelineMetrics {
  return pipelineByStatus(
    subtreeDeliveries(email, orgRows, byOwner),
    targetsByDelivery,
    amountByOpp,
  );
}

/** Compact USD for the pipeline KPIs/columns (e.g. `$1.2M`, `$45.0K`, `$0`). */
export function formatPipelineUsd(amount: number): string {
  const n = Number.isFinite(amount) ? amount : 0;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${Math.round(abs)}`;
}
